import { useCallback, useEffect, useMemo, useState } from "react";
import { newFailures, onlyKnown } from "../components/knownProblems";
import { campaignBuildKey, campaignBuildName } from "../components/campaignBuild";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useTitle } from "../useTitle";
import { ApiError, ago, api, type Campaign, type Project } from "../api";
import { Board, Group, Loading, Notice, PageHead, Row, Status, type Tone, Views } from "../components/Look";
import { useTally } from "./Campaign";

/**
 * Every campaign, across the benches somebody may see.
 *
 * THE SERVICE KNEW AND COULD NOT BE ASKED. A campaign was reachable from the
 * bench that ran it (its last three), from Now (the newest of each build) and
 * by typing its number into the address bar. "How did this project do?" - the
 * question campaigns exist to answer - had no page, so the runs list looked
 * like the whole story while every campaign sat one link away from nowhere.
 *
 * Grouped by BUILD, not by day. A campaign is a verdict on a build: reading
 * "7.2.72: green, then not green after the rerun" is the point, and a date
 * heading would scatter the three campaigns of one build across three days.
 *
 * The project and bench filters are the SERVER's (`?project_id=`, `?bench_id=`),
 * because the newest twenty campaigns of every bench, filtered here, is twenty
 * rows of somebody else's bench and none of yours. The outcome filter is this
 * page's own, over what has been loaded, and the footer says so.
 */

const PAGE = 20;

type Outcome = "" | "not_green" | "green" | "running";

export default function Campaigns() {
  const { t } = useTranslation();
  useTitle(t("campaigns.title"));
  const [params, setParams] = useSearchParams();
  const project = params.get("project") ?? "";
  const bench = params.get("bench") ?? "";
  const [list, setList] = useState<Campaign[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [outcome, setOutcome] = useState<Outcome>("");
  const [limit, setLimit] = useState(PAGE);
  const [more, setMore] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.campaigns({
        project: project ? Number(project) : undefined,
        bench: bench ? Number(bench) : undefined,
        limit,
      });
      setList(d.campaigns);
      // A full page back means there is probably another; a short one is the
      // end. The endpoint returns no total, and a total invented here would go
      // wrong exactly on the boundary.
      setMore(d.campaigns.length >= limit);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoaded(true);
    }
  }, [project, bench, limit]);

  useEffect(() => {
    let live = true;
    const tick = () => { if (live) void load(); };
    tick();
    // Only a running campaign changes under the reader; the interval is cheap
    // enough to keep either way and the page is a wall display as often as not.
    const h = setInterval(tick, 15000);
    return () => { live = false; clearInterval(h); };
  }, [load]);

  useEffect(() => {
    api.projects().then((d) => setProjects(d.projects)).catch(() => {});
  }, []);

  function pick(key: "project" | "bench", value: string) {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value); else p.delete(key);
    // A bench belongs to one project, so changing the project cannot keep a
    // bench filter that no longer sits inside it.
    if (key === "project") p.delete("bench");
    setParams(p);
    setLimit(PAGE);
  }

  const shown = outcome ? list.filter((c) => c.status === outcome) : list;
  const newest = shown[0];
  const benchOf = (c: Campaign) => c.bench_name ?? `#${c.bench_id}`;

  return (
    <section>
      <PageHead
        title={t("campaigns.heading")}
        answer={loaded ? <Answer c={newest} bench={newest && benchOf(newest)} /> : undefined}
        bad={!!newest && newest.status === "not_green"}
        after={list.length > 0 ? <p>{t("campaigns.loaded", { count: list.length })}</p> : undefined}
      />
      {!loaded && <Loading label={t("common.loading")} />}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Views
          label={t("campaigns.projectViews")}
          value={project}
          onChange={(v) => pick("project", v)}
          items={[["", t("campaigns.allProjects")] as [string, string]].concat(
            projects.map((p) => [String(p.id), p.name] as [string, string]),
          )}
        />
        <Views
          label={t("campaigns.outcomeViews")}
          value={outcome}
          onChange={setOutcome}
          items={[
            ["", t("campaigns.any")],
            ["not_green", t("campaigns.onlyNotGreen")],
            ["green", t("campaigns.onlyGreen")],
            ["running", t("campaigns.onlyRunning")],
          ] as [Outcome, string][]}
        />
      </div>

      {bench && (
        <Notice>
          {t("campaigns.oneBench", { bench: list[0] ? benchOf(list[0]) : `#${bench}` })}{" "}
          <button type="button" className="underline" onClick={() => pick("bench", "")}>
            {t("campaigns.showEveryBench")}
          </button>
        </Notice>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      {loaded && shown.length === 0 && !error && (
        <p className="text-base text-slate-500">
          {list.length === 0 ? t("campaigns.empty") : t("campaigns.noneMatch")}
        </p>
      )}

      {shown.length > 0 && <Builds list={shown} />}

      {more && (
        <p className="mt-4">
          <button type="button" className="btn-quiet" onClick={() => setLimit((n) => n + PAGE)}>
            {t("campaigns.loadMore", { count: PAGE })}
          </button>
        </p>
      )}
      {outcome && (
        <p className="mt-4 text-sm text-slate-500">{t("campaigns.filterNote", { count: list.length })}</p>
      )}
    </section>
  );
}

/** The newest campaign in view, in one sentence. */
function Answer({ c, bench }: { c?: Campaign; bench?: string }) {
  const { t } = useTranslation();
  if (!c) return <span data-testid="page-answer-text">{t("campaigns.answerNone")}</span>;
  const v = { bench: bench ?? "", build: campaignBuildName(c) };
  const text =
    c.status === "running" ? t("campaigns.answerRunning", v)
      : c.status === "cancelled" ? t("campaigns.answerCancelled", v)
        : c.status === "green" ? t("campaigns.answerGreen", v)
          : onlyKnown(c) ? t("known.onlyKnownNewest", { n: c.known })
            : newFailures(c) ? t("campaigns.answerFailed", { ...v, count: newFailures(c) })
              : c.lost ? t("campaigns.answerLost", v)
                : t("campaigns.answerNotAllRan", v);
  return <span data-testid="page-answer-text">{text}</span>;
}

/** The campaigns of one build together, newest build first. */
function Builds({ list }: { list: Campaign[] }) {
  const { t } = useTranslation();
  const groups = useMemo(() => byBuild(list), [list]);
  return (
    <Board>
      {groups.map(([key, cs]) => (
        <div key={key}>
          <Group>
            {cs[0].build_ref
              ? t("campaigns.buildGroup", { build: campaignBuildName(cs[0]), count: cs.length })
              : t("campaigns.installedGroup", { bench: cs[0].bench_name ?? `#${cs[0].bench_id}`, count: cs.length })}
          </Group>
          {cs.map((c) => <CampaignRow key={c.id} c={c} />)}
        </div>
      ))}
    </Board>
  );
}

/** One campaign: how it went, where, what it came to, and when. */
function CampaignRow({ c }: { c: Campaign }) {
  const { t } = useTranslation();
  const tally = useTally(c.planned, c);
  const tone: Tone =
    c.status === "green" ? "good"
      : c.status === "running" ? "live"
        : c.status === "cancelled" ? "off"
          : "bad";
  const word =
    c.status === "green" ? t("campaign.status.green")
      : c.status === "running" ? t("campaign.status.running")
        : c.status === "cancelled" ? t("campaign.status.cancelled")
          : t("campaign.status.not_green");
  const origin = c.origin && c.origin !== "manual"
    ? t(`campaigns.origin.${c.origin}` as "campaigns.origin.nightly")
    : t("campaigns.origin.manual", { who: c.requested_by });
  return (
    <Row
      status={<Status tone={tone}>{word}</Status>}
      title={
        <Link className="font-semibold hover:underline" to={`/campaigns/${c.id}`}>
          {t("campaigns.rowTitle", { id: c.id, bench: c.bench_name ?? `#${c.bench_id}` })}
        </Link>
      }
      sub={tally.map((x) => x.label).join(", ")}
      aside={
        <span className="whitespace-nowrap">
          {origin}, {ago(c.created_at)}
        </span>
      }
    />
  );
}

/** Campaigns by the build they judged, newest build first, newest first inside.
 *  A campaign that named no build belongs to the bench it ran on: "whatever is
 *  installed there" is a different thing on every bench. */
export function byBuild(list: Campaign[]): [string, Campaign[]][] {
  const out = new Map<string, Campaign[]>();
  for (const c of [...list].sort((a, b) => b.created_at - a.created_at || b.id - a.id)) {
    const key = campaignBuildKey(c);
    const at = out.get(key);
    if (at) at.push(c); else out.set(key, [c]);
  }
  return [...out.entries()];
}

